import styled from 'styled-components';
import useNetworkStatus from '../hooks/useCheckNetwork';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Disconnected } from '../assets/images/icons/signal_disconnected.svg';
import { useState, useEffect, useMemo, useRef } from 'react';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5010;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  user-select: none;
  .modal {
    max-width: calc(100vw - 2rem);
    background-color: var(--board);
    border-radius: 0.5rem;
    overflow: hidden;
    max-height: calc(100vh - 2rem);
    box-shadow: 0 0 1rem 0 rgba(0, 0, 0, 0.2);
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: var(--filled-red-deep);
      color: var(--bw-inverse);
      padding: 0.5rem;
      .title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem;
        .titleName {
          font-size: 1.125rem;
          font-weight: 400;
          padding: 0 0.5rem;
        }
      }
    }
    .body {
      background-color: var(--board);
      padding: 0.5rem;
      margin: 1.5rem 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.5rem;
      overflow: auto;
      max-height: 19.75rem;
      span {
        line-height: 1.25;
        text-align: center;
      }

      &.msg {
        padding: 2rem 4rem 2rem 4rem;
        font-size: 1.5rem;
        color: var(--text-primary) !important;
        font-weight: 600;

        .smallText {
          font-size: 1.125rem;
          color: var(--text-secondary) !important;
          margin-top: 0.5rem;
        }
      }

      @media (min-width: 768px) {
        max-height: 28.75rem;
      }
      @media (min-width: 1025px) {
        max-height: 42.75rem;
      }
    }
  }
`;

const MODAL_DELAY_MS = 5000;

const NetworkStatusIndicator = () => {
  const { t } = useTranslation();
  const isOnline = useNetworkStatus();
  const [showIndicator, setShowIndicator] = useState(false);
  const isAndroidTV = useMemo(() => /Android TV|AndroidTV|Smart TV|SmartTV/i.test(navigator.userAgent), []); // UA 고정
  const onlineRef = useRef(isOnline); // 최신 online 상태 스냅샷 보관 (비동기 타이머 콜백에서 참조)
  const resumeCooldownRef = useRef(false); // '깨움'(절전→활성) 직후 false positive 방지를 위한 쿨다운 플래그
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // onlineRef 최신화
  useEffect(() => {
    onlineRef.current = isOnline;
  }, [isOnline]);

  // visibilitychange 이벤트 감지 → 화면 다시 보일 때 1초간 쿨다운 모드
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        resumeCooldownRef.current = true;
        if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = setTimeout(() => {
          resumeCooldownRef.current = false;
          resumeTimerRef.current = null;
        }, 1000);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, []);

  // 표시 로직: offline 판정 → 모달 띄우기
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const checkAndMaybeShow = () => {
      if (cancelled) return;

      // 이미 online이면 중단
      if (onlineRef.current) return;

      // 쿨다운 중이면 600ms 후 다시 확인
      if (resumeCooldownRef.current) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(checkAndMaybeShow, 600); // 쿨다운 잔여 시간보다 짧게 폴링
        return;
      }
      // navigator.onLine 기준 최종 오프라인 판정
      const stillOffline = !navigator.onLine;
      if (stillOffline && !cancelled) setShowIndicator(true);
    };

    if (!isOnline) {
      // Android TV는 5초 지연 후 노출, 그 외는 즉시 노출
      const delay = isAndroidTV ? MODAL_DELAY_MS : 0;
      timer = setTimeout(checkAndMaybeShow, delay);
    } else {
      // online이면 모달 즉시 숨김
      setShowIndicator(false);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isOnline, isAndroidTV]);

  if (isOnline || !showIndicator) return null;

  return (
    <Root>
      <div className='modal' role='dialog' aria-live='assertive' aria-label={t('네트워크 오류')}>
        <div className='header'>
          <div className='title'>
            <Disconnected fill='white' width={32} height={32} />
            <span className='titleName'>{t('네트워크 오류')}</span>
          </div>
        </div>
        <div className='body msg'>
          <div className='flex-col'>
            <span>{t('네트워크 오류로 인해 요청이 처리되지 않았습니다')}.</span>
          </div>
          <div className='smallText'>{t('네트워크 연결 상태를 확인하세요')}.</div>
        </div>
      </div>
    </Root>
  );
};

export default NetworkStatusIndicator;
